/* eslint-disable no-unused-vars */
import React, { useState, useMemo, useEffect } from "react";
import { Button, Modal, Form, Tab, Tabs, Badge, Alert } from "react-bootstrap";
import { FaFileInvoice, FaUserCheck, FaUsers, FaReceipt, FaTrash } from "react-icons/fa";
import Select from 'react-select';
import { useGenerateKRAEtimsInvoiceMutation } from "../../../lib/apiSlice/tenant/tenantSlice.tsx";
import { useAddTypeCInvoicingMutation, useDeleteInvoiceMutation, useLazyGetMissingEtimsInvoicesQuery } from "../../../lib/apiSlice/invoicing/invoicingSlice.tsx";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useGetPropertySettingsSummaryQuery, useGetCompanyPropertyDetailsQuery } from "../../../lib/apiSlice/property/propertySlice.tsx";

export default function EtimsInvoiceGeneration() {
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState("single");
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [includeKra, setIncludeKra] = useState(true);
    const [includeSMS, setIncludeSMS] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [tenantsDetails, setTenantDetails] = useState([]);
    const [selectedInvoiceMonth, setSelectedInvoiceMonth] = useState('')
    const [selectedInvoiceYear, setSelectedInvoiceYear] = useState('')
    const [selectedCategoryType, setSelectedCategoryType] = useState('monthly')
    const [penalty_description, setPenaltyDecription] = useState('')
    const [penalty_amount, setPenaltyAmount] = useState('')
    const [penalties, setPenalties] = useState([]);
    const [showMissingModal, setShowMissingModal] = useState(false);
    const monthOptions = [
        "january", "february", "march", "april", "may", "june",
        "july", "august", "september", "october", "november", "december"
    ];
    const now = new Date();
    const defaultMonth = monthOptions[now.getMonth()];
    const defaultYear = now.getFullYear();
    const [missingMonth, setMissingMonth] = useState(defaultMonth);
    const [missingYear, setMissingYear] = useState(String(defaultYear));
    const [activeMissingCategory, setActiveMissingCategory] = useState('B');
    const [selectedMissingIds, setSelectedMissingIds] = useState([]);
    const [failedInvoices, setFailedInvoices] = useState([]);
    const [showFailedModal, setShowFailedModal] = useState(false);
    const [generationSummary, setGenerationSummary] = useState(null);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [showDeleteSummaryModal, setShowDeleteSummaryModal] = useState(false);
    const [isDeletingMissingInvoices, setIsDeletingMissingInvoices] = useState(false);
    const [deleteSummary, setDeleteSummary] = useState(null);
    const [failedDeletes, setFailedDeletes] = useState([]);
    const [missingSendSms, setMissingSendSms] = useState(true);


    const { id } = useParams()

    const { data } = useGetPropertySettingsSummaryQuery(id)
    const { data: companyData } = useGetCompanyPropertyDetailsQuery();
    const [fetchMissingEtims, { data: missingEtimsData, isFetching: isMissingLoading }] = useLazyGetMissingEtimsInvoicesQuery();
    const [addMissingInvoices, { isLoading: isRegenerating }] = useAddTypeCInvoicingMutation();
    const [deleteInvoice] = useDeleteInvoiceMutation();

    useEffect(() => {
        setTenantDetails(data?.data?.all_tenants_info)
    }, [data])
    // Format tenants for react-select
    const tenantOptions = useMemo(() => {
        return tenantsDetails?.map(tenant => ({
            value: tenant.unit_id,
            label: `${tenant.tenant_name} (${tenant.unit})`,
            data: tenant // Include the full tenant object for reference
        }));
    }, [tenantsDetails]);

        const [invoiceData, isLoading, error] = useGenerateKRAEtimsInvoiceMutation()

    const extractErrorMessage = (err, fallbackMessage) => {
        if (typeof err?.data === "string") {
            return err.data;
        }
        return err?.data?.message || err?.data?.error || fallbackMessage;
    };

    const buildFailedInvoiceList = (attemptedInvoices = [], failedEntries = []) => {
        const attemptedInvoicesMap = new Map(
            attemptedInvoices.map((invoice) => [invoice.invoice_id, invoice])
        );

        return failedEntries.map((failedEntry, index) => ({
            ...(attemptedInvoicesMap.get(failedEntry.invoice_id) || {}),
            ...failedEntry,
            rowKey: `${failedEntry.invoice_id || "missing"}-${index}`,
        }));
    };

    const applyGenerationSummary = (response, attemptedInvoices) => {
        const createdCount = response.created_count ?? response.created?.length ?? 0;
        const skippedCount = response.skipped_count ?? response.skipped_existing?.length ?? 0;
        const failedEntries = response.failed || [];
        const failedList = buildFailedInvoiceList(attemptedInvoices, failedEntries);
        const failedCount = response.failed_count ?? failedList.length;

        setGenerationSummary({
            requestedCount: response.requested_count ?? attemptedInvoices.length,
            processedCount: response.processed_count ?? (createdCount + skippedCount + failedCount),
            createdCount,
            skippedCount,
            failedCount,
            message: response.message,
            notificationError: response.notification_error,
        });
        setFailedInvoices(failedList);
        setShowFailedModal(failedCount > 0);

        if (response.notification_error) {
            toast.warning("Invoice generation finished, but the failure report email could not be sent.");
        }

        return { createdCount, skippedCount, failedCount };
    };

    const handleGenerate = async () => {
        let categoryType;
        let categoryTypeKra;

        if (activeTab === 'category_a') {
            categoryType = 'quarterly';
        } else if (activeTab === 'category_b') {
            categoryType = 'monthly';
        } else if (activeTab === 'category_c') {
            categoryType = 'monthly';
            categoryTypeKra = 'nonkra';
        }

        let data = {
            send_sms: includeSMS,
            generate_etims_invoice: includeKra,
            company_id: companyData?.results?.id,
        };

        // Tab-specific logic
        if (activeTab === 'all') {
            data.apartment_id = id;
        } else if (activeTab === 'single') {
            data.penalty_description = penalty_description;
            data.penalty_amount = penalty_amount;
            data.selected_month = selectedInvoiceMonth;
            data.selected_year = selectedInvoiceYear;
            data.house_id = selectedTenant?.data?.unit_id;
            data.invoice_period = selectedCategoryType;
            data.category_type = selectedCategoryType;
            data.category_type_kra = selectedTenant?.data?.kra_compliance ? "kra" : "nonkra";
        } else {
            data.apartment_id = id;
            data.category_type = categoryType;
            data.category_type_kra = categoryTypeKra;
        }

        setIsGenerating(true);

        try {
            const response = await invoiceData(data).unwrap();
            setIsGenerating(false);
            setShowModal(false);
            toast.success('Successfully generated the invoice');
        } catch (err) {
            console.log(err);
            setIsGenerating(false);
            setShowModal(false);
            toast.error('Failed to generate the invoice');
        }
    };

    const allMissingInvoices = useMemo(() => {
        const categories = missingEtimsData?.categories || {};
        return [
            ...(categories.A?.invoices || []),
            ...(categories.B?.invoices || []),
            ...(categories.C?.invoices || []),
        ];
    }, [missingEtimsData]);
    const selectedMissingInvoices = useMemo(
        () => allMissingInvoices.filter((inv) => selectedMissingIds.includes(inv.invoice_id)),
        [allMissingInvoices, selectedMissingIds]
    );

    const toggleMissingSelect = (invoiceId) => {
        setSelectedMissingIds((prev) => {
            if (prev.includes(invoiceId)) {
                return prev.filter((id) => id !== invoiceId);
            }
            return [...prev, invoiceId];
        });
    };

    const getCategoryInvoiceIds = (categoryInvoices = []) =>
        categoryInvoices
            .map((invoice) => invoice.invoice_id)
            .filter(Boolean);

    const selectAllMissingInCategory = (categoryInvoices) => {
        const categoryIds = getCategoryInvoiceIds(categoryInvoices);
        setSelectedMissingIds((prev) => Array.from(new Set([...prev, ...categoryIds])));
    };

    const clearMissingInCategory = (categoryInvoices) => {
        const categoryIdSet = new Set(getCategoryInvoiceIds(categoryInvoices));
        setSelectedMissingIds((prev) => prev.filter((id) => !categoryIdSet.has(id)));
    };

    const handleOpenMissingModal = () => {
        setShowMissingModal(true);
        fetchMissingEtims({
            month: missingMonth || undefined,
            year: missingYear || undefined,
        });
    };

    const handleRefreshMissing = () => {
        fetchMissingEtims({
            month: missingMonth || undefined,
            year: missingYear || undefined,
        });
    };

    const handleOpenDeleteConfirm = () => {
        if (selectedMissingInvoices.length === 0) {
            toast.error("Please select at least one invoice to delete.");
            return;
        }
        setShowDeleteConfirmModal(true);
    };

    const handleRegenerateMissing = async () => {
        const selectedInvoices = allMissingInvoices.filter((inv) =>
            selectedMissingIds.includes(inv.invoice_id)
        );

        if (selectedInvoices.length === 0) {
            toast.error("Please select at least one invoice to regenerate.");
            return;
        }

        try {
            const response = await addMissingInvoices({
                invoices: selectedInvoices,
                send_sms: missingSendSms
            }).unwrap();
            const { createdCount, skippedCount, failedCount } = applyGenerationSummary(response, selectedInvoices);

            const notify = failedCount > 0 ? toast.warning : toast.success;
            notify(`Generated ${createdCount} eTIMS invoice(s). ${skippedCount} already existed. ${failedCount} failed.`);

            setSelectedMissingIds([]);
            handleRefreshMissing();
        } catch (err) {
            console.log(err);
            toast.error(extractErrorMessage(err, "Failed to regenerate eTIMS invoices."));
        }
    };

    const handleDeleteMissingInvoices = async () => {
        if (selectedMissingInvoices.length === 0) {
            toast.error("Please select at least one invoice to delete.");
            return;
        }

        setIsDeletingMissingInvoices(true);

        const deleted = [];
        const failed = [];

        try {
            for (const invoice of selectedMissingInvoices) {
                try {
                    await deleteInvoice(invoice.invoice_id).unwrap();
                    deleted.push(invoice);
                } catch (err) {
                    failed.push({
                        ...invoice,
                        error: extractErrorMessage(err, "Failed to delete invoice."),
                    });
                }
            }

            setDeleteSummary({
                requestedCount: selectedMissingInvoices.length,
                deletedCount: deleted.length,
                failedCount: failed.length,
            });
            setFailedDeletes(failed);
            setShowDeleteSummaryModal(true);

            const notify = failed.length > 0 ? toast.warning : toast.success;
            notify(`Deleted ${deleted.length} invoice(s). ${failed.length} failed.`);

            setSelectedMissingIds(failed.map((invoice) => invoice.invoice_id).filter(Boolean));
            handleRefreshMissing();
        } finally {
            setIsDeletingMissingInvoices(false);
            setShowDeleteConfirmModal(false);
        }
    };

    const handleRetryFailed = async () => {
        if (failedInvoices.length === 0) {
            return;
        }

        try {
            const response = await addMissingInvoices({
                invoices: failedInvoices,
                send_sms: missingSendSms
            }).unwrap();
            const { createdCount, skippedCount, failedCount } = applyGenerationSummary(response, failedInvoices);

            const notify = failedCount > 0 ? toast.warning : toast.success;
            notify(`Generated ${createdCount} eTIMS invoice(s). ${skippedCount} already existed. ${failedCount} failed.`);

            if (failedCount === 0) {
                setShowFailedModal(false);
                setFailedInvoices([]);
                setGenerationSummary(null);
            }

            handleRefreshMissing();
        } catch (err) {
            console.log(err);
            toast.error(extractErrorMessage(err, "Failed to regenerate eTIMS invoices."));
        }
    };




    // Custom styling for react-select
    const customStyles = {
        control: (provided) => ({
            ...provided,
            minHeight: '38px',
            height: '38px',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#2684FF' : 'white',
            color: state.isSelected ? 'white' : 'black',
            ':active': {
                backgroundColor: state.isSelected ? '#2684FF' : '#DEEBFF',
            },
        }),
    };

    return (
        <div className="mt-5 mb-4">
            <div className="flex justify-end">
                <Link to="/category-c">
                    <button className="me-3 text-white btn-info btn">
                        Generate Category C Etims
                    </button>
                </Link>
                <Button
                    variant="outline-primary"
                    onClick={handleOpenMissingModal}
                    className="me-3"
                >
                    Missing eTIMS
                </Button>
                <Button
                    variant="primary"
                    onClick={() => setShowModal(true)}
                    className="d-flex align-items-center gap-2"
                >
                    <FaFileInvoice /> Generate Tenant Invoice
                </Button>
            </div>

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title className="d-flex align-items-center gap-2">
                        <FaFileInvoice size={24} />
                        <span>Invoice Generation</span>

                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => {
                            setActiveTab(k);
                            setSelectedTenant(null); // Reset selection when changing tabs
                        }}
                        className="mb-4"
                    >
                        <Tab
                            eventKey="single"
                            title={
                                <span className="d-flex align-items-center gap-2">
                                    <FaUserCheck /> Single Tenant
                                </span>
                            }
                        >
                            <div className="mt-3 p-3 border rounded">
                                {/* Tenant Selection */}
                                <Form.Group className="mb-3">
                                    <p>Select Tenant</p>
                                    <Select
                                        value={selectedTenant}
                                        onChange={(selectedOption) => {
                                            setSelectedTenant(selectedOption);
                                            setSelectedInvoiceMonth("");
                                            setSelectedInvoiceYear("");
                                            setSelectedCategoryType(selectedOption?.data?.generate_invoice_schedule || "monthly");
                                            setPenalties([]); // reset penalties when tenant changes
                                        }}
                                        options={tenantOptions}
                                        placeholder="Search"
                                        isSearchable
                                        styles={customStyles}
                                        noOptionsMessage={() => "No tenants found"}
                                        formatOptionLabel={(option) => (
                                            <div>
                                                {option.label}
                                                {option.data.kra_compliance ? (
                                                    <Badge bg="success" className="ms-2">Category A</Badge>
                                                ) : (
                                                    <Badge bg="warning" className="ms-2">Category C</Badge>
                                                )}
                                            </div>
                                        )}
                                    />
                                </Form.Group>

                                {/* Tenant details if selected */}
                                {selectedTenant && (
                                    <>
                                        {/* Month & Year Selection */}
                                    <Form.Group className="mb-3">
  <p className="fw-bold">Invoice Month</p>
  <Form.Select
    value={selectedInvoiceMonth}
    onChange={(e) => setSelectedInvoiceMonth(e.target.value)}
  >
    <option value="">Select Month</option>
    {[
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ].map((m, i) => (
      <option key={i} value={m.toLowerCase()}>
        {m}
      </option>
    ))}
  </Form.Select>
</Form.Group>


                                        <Form.Group className="mb-3">
                                            <p className="fw-bold">Invoice Year</p>
                                            <Form.Control
                                                type="number"
                                                min="2000"
                                                max="2100"
                                                value={selectedInvoiceYear}
                                                onChange={(e) => setSelectedInvoiceYear(e.target.value)}
                                                placeholder="Enter Year e.g. 2025"
                                            />
                                        </Form.Group>



                                        {/* Invoice period is now automatically handled based on house schedule */}

                                        {/* Penalties Section */}
                                        <Form.Group className="mb-3">
                                            <p className="fw-bold">Penalties (if any)</p>
                                            <div className="d-flex gap-2 mb-2">
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Penalty Description"
                                                    value={penalty_description}
                                                    onChange={(e) => {
                                                        setPenaltyDecription(e.target.value)
                                                    }}
                                                />
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Amount"
                                                    value={penalty_amount}
                                                    onChange={(e) => {
                                                        setPenaltyAmount(e.target.value)

                                                    }}
                                                />

                                            </div>


                                        </Form.Group>

                                        {/* Tenant KRA/SMS Toggles */}
                                        <Form.Group className="d-flex mb-3">
                                            <Form.Check
                                                type="switch"
                                                id="include-kra-single"
                                                checked={includeKra}
                                                onChange={(e) => setIncludeKra(e.target.checked)}
                                                className="d-flex align-items-center gap-2"
                                            />
                                            <p className="ms-2 mb-0 text-dark">Generate eTIMS Invoice</p>
                                        </Form.Group>

                                        <Form.Group className="d-flex mb-3">
                                            <Form.Check
                                                type="switch"
                                                id="include-sms"
                                                checked={includeSMS}
                                                onChange={(e) => setIncludeSMS(e.target.checked)}
                                                className="d-flex align-items-center gap-2"
                                            />
                                            <p className="ms-2 mb-0 text-dark">Send SMS with invoice link</p>
                                        </Form.Group>

                                        {!selectedTenant.data.kra_compliance && (
                                            <Alert variant="warning" className="mt-2">
                                                This tenant has no KRA PIN registered. If you generate eTIMS, the customer PIN will be sent as a blank string.
                                            </Alert>
                                        )}
                                    </>
                                )}
                            </div>
                        </Tab>







                        <Tab
                            eventKey="category_a"
                            title={
                                <span className="d-flex align-items-center gap-2">
                                    <FaUserCheck /> Categry A tenants
                                </span>
                            }
                        >
                            <div className="mt-3 p-3 border rounded">
                                <Alert variant="info" className="d-flex align-items-center gap-2">
                                    <FaUserCheck />
                                    This will generate invoices for all active tenants in <b>Category A</b>(Quaretly Compliant Tenants)
                                </Alert>
                                <Form.Group className="d-flex mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="include-kra-all"
                                        checked={includeKra}
                                        onChange={(e) => setIncludeKra(e.target.checked)}
                                        className="d-flex align-items-center gap-2"
                                    />
                                    <p className="d-flex align-items-center gap-2 ms-2 mb-0 p-0 border-0 w-auto text-dark">
                                        Include KRA Invoices (for compliant tenants)
                                    </p>
                                </Form.Group>

                                <Form.Group className="d-flex mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="include-sms-category-a"
                                        checked={includeSMS}
                                        onChange={(e) => setIncludeSMS(e.target.checked)}
                                        className="d-flex align-items-center gap-2"
                                    />
                                    <p className="d-flex align-items-center gap-2 ms-2 mb-0 p-0 border-0 w-auto text-dark">
                                        Send SMS to tenants with an invoice link
                                    </p>
                                </Form.Group>
                            </div>
                        </Tab>

                        <Tab
                            eventKey="category_b"
                            title={
                                <span className="d-flex align-items-center gap-2">
                                    <FaUserCheck /> Category B  Tenants
                                </span>
                            }
                        >
                            <div className="mt-3 p-3 border rounded">
                                <Alert variant="info" className="d-flex align-items-center gap-2">
                                    <FaUserCheck />
                                    This will generate invoices for all active tenants in <b>Category B (Monthly KRA Compliance Tenants)</b>
                                </Alert>
                                <Form.Group className="d-flex mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="include-kra-all"
                                        checked={includeKra}
                                        onChange={(e) => setIncludeKra(e.target.checked)}
                                        className="d-flex align-items-center gap-2"
                                    />
                                    <p className="d-flex align-items-center gap-2 ms-2 mb-0 p-0 border-0 w-auto text-dark">
                                        Include KRA Invoices (for compliant tenants)
                                    </p>
                                </Form.Group>

                                <Form.Group className="d-flex mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="include-kra-all"
                                        checked={includeSMS}
                                        onChange={(e) => setIncludeSMS(e.target.checked)}
                                        className="d-flex align-items-center gap-2"
                                    />
                                    <p className="d-flex align-items-center gap-2 ms-2 mb-0 p-0 border-0 w-auto text-dark">
                                        Send SMS to tenants with an invoice link
                                    </p>
                                </Form.Group>
                            </div>
                        </Tab>


                        <Tab
                            eventKey="category_c"
                            title={
                                <span className="d-flex align-items-center gap-2">
                                    <FaUserCheck /> Category C  Tenants
                                </span>
                            }
                        >
                            <div className="mt-3 p-3 border rounded">
                                <Alert variant="info" className="d-flex align-items-center gap-2">
                                    <FaUserCheck />
                                    This will generate invoices for all active tenants in <b>Category C (Monthly NON-KRA Compliance Tenants)</b>
                                </Alert>

                                <Form.Group className="d-flex mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="include-kra-category-c"
                                        checked={includeKra}
                                        onChange={(e) => setIncludeKra(e.target.checked)}
                                        className="d-flex align-items-center gap-2"
                                    />
                                    <p className="d-flex align-items-center gap-2 ms-2 mb-0 p-0 border-0 w-auto text-dark">
                                        Generate eTIMS Invoices (blank customer PIN when missing)
                                    </p>
                                </Form.Group>

                                <Alert variant="warning" className="d-flex align-items-center gap-2">
                                    <FaReceipt />
                                    Category C tenants without KRA PINs will be sent to eTIMS with an empty customer PIN.
                                </Alert>

                                <Form.Group className="d-flex mb-3">
                                    <Form.Check
                                        type="switch"
                                        id="include-sms-category-c"
                                        checked={includeSMS}
                                        onChange={(e) => setIncludeSMS(e.target.checked)}
                                        className="d-flex align-items-center gap-2"
                                    />
                                    <p className="d-flex align-items-center gap-2 ms-2 mb-0 p-0 border-0 w-auto text-dark">
                                        Send SMS to tenants with an invoice link
                                    </p>
                                </Form.Group>
                            </div>
                        </Tab>



                    </Tabs>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleGenerate}
                        disabled={
                            isGenerating ||
                            (activeTab === "single" && !selectedTenant)
                        }
                    >
                        {isGenerating ? (
                            <>
                                <span
                                    className="me-2 spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                ></span>
                                Generating...
                            </>
                        ) : (
                            <div className="d-flex align-items-center gap-2">
                                <FaReceipt className="me-2" />
                                Generate Invoice{activeTab === "all" ? "s" : ""}
                            </div>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Missing eTIMS Modal */}
            <Modal
                show={showMissingModal}
                onHide={() => {
                    setShowMissingModal(false);
                    setSelectedMissingIds([]);
                }}
                size="xl"
                centered
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>Missing eTIMS Invoices</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                        <Form.Select
                            value={missingMonth}
                            onChange={(e) => setMissingMonth(e.target.value)}
                            style={{ maxWidth: "200px" }}
                        >
                            {monthOptions.map((m) => (
                                <option key={m} value={m}>
                                    {m.charAt(0).toUpperCase() + m.slice(1)}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Select
                            value={missingYear}
                            onChange={(e) => setMissingYear(e.target.value)}
                            style={{ maxWidth: "160px" }}
                        >
                            {Array.from({ length: 7 }).map((_, idx) => {
                                const y = defaultYear - 3 + idx;
                                return (
                                    <option key={y} value={String(y)}>
                                        {y}
                                    </option>
                                );
                            })}
                        </Form.Select>
                        <Button variant="outline-primary" onClick={handleRefreshMissing} disabled={isMissingLoading}>
                            {isMissingLoading ? "Loading..." : "Refresh"}
                        </Button>
                        <Form.Check
                            type="switch"
                            id="missing-send-sms"
                            label="Send SMS"
                            checked={missingSendSms}
                            onChange={(e) => setMissingSendSms(e.target.checked)}
                            className="ms-2"
                        />
                        {missingEtimsData?.month && missingEtimsData?.year && (
                            <span className="text-muted d-flex align-items-center">
                                Showing {missingEtimsData.month} {missingEtimsData.year}
                            </span>
                        )}
                    </div>

                    <Tabs
                        activeKey={activeMissingCategory}
                        onSelect={(k) => setActiveMissingCategory(k || "B")}
                        className="mb-3"
                    >
                        {["A", "B", "C"].map((cat) => {
                            const categoryInvoices = missingEtimsData?.categories?.[cat]?.invoices || [];
                            const selectedInCategoryCount = categoryInvoices.filter((inv) =>
                                selectedMissingIds.includes(inv.invoice_id)
                            ).length;
                            return (
                                <Tab
                                    key={cat}
                                    eventKey={cat}
                                    title={`Category ${cat} (${missingEtimsData?.categories?.[cat]?.count || 0})`}
                                >
                                    <div className="border rounded">
                                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 p-3 border-bottom bg-light">
                                            <span className="text-muted small">
                                                {selectedInCategoryCount} of {categoryInvoices.length} selected in Category {cat}
                                            </span>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    disabled={categoryInvoices.length === 0}
                                                    onClick={() => selectAllMissingInCategory(categoryInvoices)}
                                                >
                                                    Select All
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline-secondary"
                                                    disabled={selectedInCategoryCount === 0}
                                                    onClick={() => clearMissingInCategory(categoryInvoices)}
                                                >
                                                    Clear Category
                                                </Button>
                                            </div>
                                        </div>
                                        <table className="table mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: "40px" }}></th>
                                                    <th>Tenant</th>
                                                    <th>Unit</th>
                                                    <th>Property</th>
                                                    <th>Period</th>
                                                    <th>Month</th>
                                                    <th>Amount</th>
                                                    <th>KRA PIN</th>
                                                    <th>Eligible</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {categoryInvoices.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={9} className="text-center text-muted py-3">
                                                            No missing invoices for this category.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    categoryInvoices.map((inv) => (
                                                        <tr key={inv.invoice_id}>
                                                            <td>
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    checked={selectedMissingIds.includes(inv.invoice_id)}
                                                                    onChange={() => toggleMissingSelect(inv.invoice_id)}
                                                                />
                                                            </td>
                                                            <td>{inv.user_name}</td>
                                                            <td>{inv.house_number}</td>
                                                            <td>{inv.apartment_name}</td>
                                                            <td>{inv.invoice_period || "-"}</td>
                                                            <td>{inv.invoice_month} {inv.invoice_year}</td>
                                                            <td>KES {Number(inv.amount).toLocaleString()}</td>
                                                            <td>{inv.kra_pin === "" ? "Blank" : inv.kra_pin || "N/A"}</td>
                                                            <td>
                                                                {inv.eligible_for_etims ? (
                                                                    <Badge bg="success">Yes</Badge>
                                                                ) : (
                                                                    <Badge bg="warning">No</Badge>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </Tab>
                            );
                        })}
                    </Tabs>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="secondary" onClick={() => setShowMissingModal(false)}>
                        Close
                    </Button>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-danger"
                            onClick={handleOpenDeleteConfirm}
                            disabled={isRegenerating || isDeletingMissingInvoices || selectedMissingIds.length === 0}
                        >
                            {isDeletingMissingInvoices ? "Deleting..." : `Delete Selected (${selectedMissingIds.length})`}
                        </Button>
                        <Button variant="primary" onClick={handleRegenerateMissing} disabled={isRegenerating || isDeletingMissingInvoices}>
                            {isRegenerating ? "Regenerating..." : `Regenerate Selected (${selectedMissingIds.length})`}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showDeleteConfirmModal}
                onHide={() => !isDeletingMissingInvoices && setShowDeleteConfirmModal(false)}
                centered
            >
                <Modal.Header closeButton={!isDeletingMissingInvoices} className="bg-light">
                    <Modal.Title className="d-flex align-items-center gap-2">
                        <FaTrash />
                        Delete Selected Invoices
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">
                        Delete <strong>{selectedMissingInvoices.length}</strong> selected tenant invoice(s)?
                    </p>
                    <p className="text-muted mb-0">
                        This removes the tenant invoice records. If any selected invoice has linked payments or reconciliation records,
                        those records may also be rolled back by the backend delete flow.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteConfirmModal(false)}
                        disabled={isDeletingMissingInvoices}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleDeleteMissingInvoices}
                        disabled={isDeletingMissingInvoices}
                    >
                        {isDeletingMissingInvoices ? "Deleting..." : "Delete Invoices"}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showDeleteSummaryModal}
                onHide={() => setShowDeleteSummaryModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>Invoice Deletion Summary</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deleteSummary && (
                        <Alert variant={deleteSummary.failedCount > 0 ? "warning" : "success"}>
                            <div><strong>Deleted:</strong> {deleteSummary.deletedCount} invoice(s)</div>
                            <div><strong>Failed:</strong> {deleteSummary.failedCount} invoice(s)</div>
                            <div><strong>Processed:</strong> {deleteSummary.deletedCount + deleteSummary.failedCount} of {deleteSummary.requestedCount}</div>
                        </Alert>
                    )}
                    {failedDeletes.length === 0 ? (
                        <p className="text-muted mb-0">All selected invoices were deleted.</p>
                    ) : (
                        <div className="border rounded">
                            <table className="table mb-0">
                                <thead>
                                    <tr>
                                        <th>Invoice ID</th>
                                        <th>Tenant</th>
                                        <th>Unit</th>
                                        <th>Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {failedDeletes.map((inv, index) => (
                                        <tr key={`${inv.invoice_id || "missing"}-${index}`}>
                                            <td>{inv.invoice_id}</td>
                                            <td>{inv.user_name}</td>
                                            <td>{inv.house_number}</td>
                                            <td>{inv.error || "Failed to delete"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteSummaryModal(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Failed Invoices Modal */}
            <Modal
                show={showFailedModal}
                onHide={() => setShowFailedModal(false)}
                size="lg"
                centered
            >
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>eTIMS Generation Summary</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {generationSummary && (
                        <Alert variant={generationSummary.failedCount > 0 ? "warning" : "success"}>
                            <div><strong>Generated:</strong> {generationSummary.createdCount} invoice(s)</div>
                            <div><strong>Failed:</strong> {generationSummary.failedCount} invoice(s)</div>
                            <div><strong>Already existed:</strong> {generationSummary.skippedCount} invoice(s)</div>
                            <div><strong>Processed:</strong> {generationSummary.processedCount} of {generationSummary.requestedCount}</div>
                        </Alert>
                    )}
                    {failedInvoices.length === 0 ? (
                        <p className="text-muted mb-0">No failed invoices.</p>
                    ) : (
                        <div className="border rounded">
                            <table className="table mb-0">
                                <thead>
                                    <tr>
                                        <th>Invoice ID</th>
                                        <th>Tenant</th>
                                        <th>Unit</th>
                                        <th>Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {failedInvoices.map((inv) => (
                                        <tr key={inv.rowKey || inv.invoice_id}>
                                            <td>{inv.invoice_id}</td>
                                            <td>{inv.user_name}</td>
                                            <td>{inv.house_number}</td>
                                            <td>{inv.error || "Not generated"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="secondary" onClick={() => setShowFailedModal(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleRetryFailed} disabled={isRegenerating || failedInvoices.length === 0}>
                        {isRegenerating ? "Retrying..." : "Retry Failed"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
